// Frontend Error Logging and Debugging System
// SAFE VERSION - Never consumes response bodies, never throws
// Use window.location.origin for production to avoid stale env variable issues
const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? window.location.origin 
  : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');

// Flag to track if we're inside error logging (prevent recursion)
let isLoggingError = false;

// Flag to disable body reading if monitoring tools are detected
let disableBodyReading = false;

class ErrorLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500;
    this.setupGlobalErrorHandler();
    this.detectMonitoringTools();
  }

  detectMonitoringTools() {
    // Detect if rrweb or other monitoring tools have wrapped fetch
    try {
      const fetchStr = window.fetch?.toString() || '';
      if (fetchStr.includes('rrweb') || fetchStr.includes('recorder') || fetchStr.includes('Proxy')) {
        console.log('[ErrorLogger] Monitoring tool detected, disabling response body reading');
        disableBodyReading = true;
      }
    } catch (e) {
      // Ignore detection errors
    }
  }

  setupGlobalErrorHandler() {
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'UNHANDLED_ERROR',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'UNHANDLED_REJECTION',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack
      });
    });

    // Capture console errors (but don't break anything)
    try {
      const originalConsoleError = console.error;
      console.error = (...args) => {
        // Call original first to ensure errors are always logged
        originalConsoleError.apply(console, args);
        
        // Then try to log (but don't throw)
        try {
          if (!isLoggingError) {
            this.logError({
              type: 'CONSOLE_ERROR',
              message: args.map(arg => {
                try {
                  return typeof arg === 'object' ? JSON.stringify(arg) : String(arg);
                } catch {
                  return '[Unserializable]';
                }
              }).join(' ')
            });
          }
        } catch (e) {
          // Never throw from error logging
        }
      };
    } catch (e) {
      // If we can't wrap console.error, that's fine
    }
  }

  logError(errorData) {
    // Prevent recursion
    if (isLoggingError) return null;
    
    try {
      isLoggingError = true;
      
      const enrichedError = {
        ...errorData,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        user: this.getCurrentUser()
      };

      // Store locally
      this.logs.push(enrichedError);
      if (this.logs.length > this.maxLogs) {
        this.logs.shift(); // Remove oldest
      }

      // Send to backend (async, don't wait, never throw)
      this.sendToBackend(enrichedError);

      return enrichedError;
    } catch (e) {
      // Never throw from error logging
      return null;
    } finally {
      isLoggingError = false;
    }
  }

  /**
   * Log API error - SAFE version that doesn't consume response body
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {number} status - HTTP status code
   * @param {Response|any} response - Response object or parsed data
   */
  logApiError(method, url, status, response) {
    try {
      // SAFETY: Never try to read response body - it may already be consumed
      // Only log metadata that's always available
      let responseInfo = '[body not captured]';
      
      if (response && typeof response === 'object') {
        // If it's already parsed data, stringify it
        if (!(response instanceof Response)) {
          try {
            responseInfo = JSON.stringify(response).slice(0, 500);
          } catch {
            responseInfo = '[unserializable]';
          }
        } else {
          // It's a Response object - only log safe properties
          responseInfo = `Response(ok=${response.ok}, bodyUsed=${response.bodyUsed})`;
        }
      }
      
      return this.logError({
        type: 'API_ERROR',
        method,
        url,
        status,
        response: responseInfo
      });
    } catch (e) {
      // Never throw
      return null;
    }
  }

  logPerformance(action, duration) {
    try {
      if (duration > 1000) { // Log slow operations (>1s)
        console.warn(`⚠️ Slow operation: ${action} took ${duration}ms`);
        this.logError({
          type: 'PERFORMANCE',
          action,
          duration,
          message: `Slow operation: ${action} (${duration}ms)`
        });
      }
    } catch (e) {
      // Never throw
    }
  }

  // Rate limiting for backend error reporting
  lastErrorSent = 0;
  errorSendingDisabled = false;
  errorSendFailures = 0;
  
  async sendToBackend(errorData) {
    // Don't send errors about sending errors (infinite loop)
    if (errorData.url?.includes('/api/debug/client-error')) {
      return;
    }
    
    // Rate limit: max 1 error per second
    const now = Date.now();
    if (now - this.lastErrorSent < 1000) {
      return;
    }
    
    // Circuit breaker: disable sending if too many failures
    if (this.errorSendingDisabled) {
      // Try to re-enable after 60 seconds
      if (now - this.lastErrorSent > 60000) {
        this.errorSendingDisabled = false;
        this.errorSendFailures = 0;
      } else {
        return;
      }
    }
    
    this.lastErrorSent = now;
    
    try {
      // Use a simple fetch without any wrappers
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${API_URL}/api/debug/client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Track failures for circuit breaker
      if (!response.ok && response.status >= 500) {
        this.errorSendFailures++;
        if (this.errorSendFailures >= 3) {
          this.errorSendingDisabled = true;
          console.debug('[ErrorLogger] Error reporting disabled after 3 failures');
        }
      } else {
        this.errorSendFailures = 0;
      }
    } catch (e) {
      // Silently fail - don't create infinite error loop
      this.errorSendFailures++;
      if (this.errorSendFailures >= 3) {
        this.errorSendingDisabled = true;
      }
    }
  }

  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.email || user.name || 'Unknown';
      }
    } catch (e) {
      // Ignore
    }
    return 'Anonymous';
  }

  getLogs(filter = null) {
    if (!filter) return this.logs;
    return this.logs.filter(log => log.type === filter);
  }

  exportLogs() {
    try {
      const dataStr = JSON.stringify(this.logs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `error-logs-${new Date().toISOString()}.json`;
      link.click();
    } catch (e) {
      console.warn('Failed to export logs:', e);
    }
  }

  clearLogs() {
    this.logs = [];
    console.log('✅ Error logs cleared');
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

// Export for use in components
export default errorLogger;

// Helper function to wrap async operations with error logging
export const withErrorLogging = async (fn, operation) => {
  const startTime = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - startTime;
    errorLogger.logPerformance(operation, duration);
    return result;
  } catch (error) {
    try {
      errorLogger.logError({
        type: 'OPERATION_ERROR',
        operation,
        message: error.message,
        stack: error.stack
      });
    } catch (e) {
      // Never throw from error logging
    }
    throw error;
  }
};
